export const formatMoney = (cents, currency = 'usd') =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(
		(cents ?? 0) / 100
	);

export const formatDate = (iso) =>
	iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const FREQUENCY = {
	weekly: { noun: 'Weekly', per: 'week' },
	bi_weekly: { noun: 'Every 2 weeks', per: '2 weeks' },
	monthly: { noun: 'Monthly', per: 'month' },
};

export const frequencyNoun = (frequency) => FREQUENCY[frequency]?.noun ?? frequency ?? '—';

/** "$1,247.50 / month" */
export const amountPerPeriod = (cents, currency, frequency) =>
	`${formatMoney(cents, currency)}${FREQUENCY[frequency] ? ` / ${FREQUENCY[frequency].per}` : ''}`;

const BRAND_LABELS = {
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: 'Amex',
	discover: 'Discover',
	diners: 'Diners',
	jcb: 'JCB',
	unionpay: 'UnionPay',
};

/**
 * "Visa •••• 4242" / "Chase •••• 6789". Bank PMs carry bank_name (list
 * endpoint) or mirror it into brand (status endpoint's default chip).
 */
export const paymentMethodLabel = (pm) => {
	if (!pm) return null;
	const name =
		pm.type === 'us_bank_account'
			? (pm.bank_name ?? 'Bank account')
			: (BRAND_LABELS[pm.brand?.toLowerCase?.()] ?? pm.brand ?? 'Card');

	return `${name} •••• ${pm.last4 ?? '????'}`;
};

/** The per-cycle amount the next invoice will actually charge. */
export const assignmentChargeCents = (assignment) => assignment?.next_charge_amount_cents ?? assignment?.amount_cents;

/**
 * Amount rows for the plan summary box. With fees/discounts on the next
 * cycle: Base amount, then indented Fees / Discounts / Total. Without
 * modifiers the breakdown is noise, so it collapses to a single Amount row.
 */
export const amountBreakdownRows = (assignment) => {
	const { currency, frequency, breakdown } = assignment ?? {};

	if (!breakdown || (!breakdown.fees_cents && !breakdown.discounts_cents)) {
		return [{ label: 'Amount', value: amountPerPeriod(assignmentChargeCents(assignment), currency, frequency) }];
	}

	return [
		{ label: 'Base amount', value: amountPerPeriod(breakdown.base_cents, currency, frequency) },
		...(breakdown.fees_cents
			? [{ label: 'Fees', value: `+ ${formatMoney(breakdown.fees_cents, currency)}`, indent: true }]
			: []),
		...(breakdown.discounts_cents
			? [{ label: 'Discounts', value: `− ${formatMoney(breakdown.discounts_cents, currency)}`, indent: true }]
			: []),
		{ label: 'Total', value: amountPerPeriod(breakdown.total_cents, currency, frequency), indent: true },
	];
};
